import { antdTokenFilter } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Form as AntForm, Button, Checkbox, Col, type FormInstance, Input, InputNumber, Row, Select, Space } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import type React from 'react';
import { TestTubeSelect } from '../../../../components/TestTubeSelect';
import {
  type SpecimenCreateInput,
  SpecimenFeature,
  SpecimenGroup,
  type SpecimenUpdateInput,
} from '../../../../graphql/generated/graphql.ts';
import { translateSpecimenFeature, translateSpecimenGroup } from '../../../../utils/enumHelpers';
import { MAX_LOCALIZATION_LENGTH } from '../../../../utils/localizationLimits.ts';
import type { Form, TestTube } from '../Specimens.tsx';
import { LocalizationFieldsEditor } from './LocalizationFieldsEditor.tsx';
import { PredefinedLocalizationsEditor } from './PredefinedLocalizationsEditor.tsx';

const { Option } = Select;

export interface AnalysisStation {
  id: string;
  name: string;
}

export type SpecimenFormProps = {
  form: FormInstance;
  initialValues?: SpecimenCreateInput;
  onFinish: (values: SpecimenCreateInput | SpecimenUpdateInput) => void;
  nameValidator: (name: string) => boolean;
  testTubes: TestTube[];
  forms: Form[];
  analysisStations: AnalysisStation[];
};

export const SpecimenForm: React.FC<SpecimenFormProps> = ({
  form,
  initialValues,
  onFinish,
  nameValidator,
  testTubes,
  forms,
  analysisStations,
}) => {
  const localizationInputEnabled = useWatch('localizationInputEnabled', form);
  const requiresLocalizations = useWatch('requiresLocalizations', form);

  return (
    <AntForm layout="vertical" form={form} initialValues={initialValues} onFinish={onFinish}>
      <Row gutter={[16, 0]}>
        <Col span={12}>
          <AntForm.Item
            name="name"
            label="Name"
            rules={[
              {
                required: true,
                whitespace: true,
              },
              {
                validator: (_, name) => (nameValidator(name) ? Promise.resolve() : Promise.reject('Dieser Name ist bereits vergeben')),
              },
            ]}
          >
            <Input autoFocus />
          </AntForm.Item>
        </Col>
        <Col span={12}>
          <AntForm.Item name="classification" label="Materialkennung">
            <Input />
          </AntForm.Item>
        </Col>
      </Row>
      <Row gutter={[16, 0]}>
        <Col span={12}>
          <AntForm.Item name="group" label="Gruppe" rules={[{ type: 'enum', enum: Object.keys(SpecimenGroup), required: true }]}>
            <Select>
              {Object.keys(SpecimenGroup).map(group => (
                <Option key={group} value={group}>
                  {translateSpecimenGroup(group)}
                </Option>
              ))}
            </Select>
          </AntForm.Item>
        </Col>
        <Col span={12}>
          <AntForm.Item
            name="storagePeriod"
            label="Lagerdauer (Stunden)"
            rules={[{ type: 'integer', min: 0, max: 1000, message: 'Bitte Anzahl angeben', required: true }]}
          >
            <InputNumber inputMode="numeric" required min={0} max={1000} step={1} style={{ width: '100%' }} />
          </AntForm.Item>
        </Col>
      </Row>
      <AntForm.Item name="testTubeIds" label="Probenbehälter" valuePropName="selected">
        <TestTubeSelect style={{ width: '100%' }} multiple testTubes={testTubes} />
      </AntForm.Item>
      <Row gutter={[16, 0]}>
        <Col span={12}>
          <AntForm.Item name="features" label="Eigenschaften">
            <Select
              showSearch={{ filterOption: antdTokenFilter }}
              allowClear
              mode="multiple"
              placeholder="Suche nach Eigenschaften"
              options={Object.keys(SpecimenFeature)
                .sort((a, b) => translateSpecimenFeature(a).localeCompare(translateSpecimenFeature(b)))
                .map(it => ({
                  label: translateSpecimenFeature(it),
                  value: it,
                  search: translateSpecimenFeature(it),
                }))}
            />
          </AntForm.Item>
        </Col>
        <Col span={12}>
          <AntForm.Item
            name="analysisStationIds"
            label="Analysestationen"
            tooltip="Die Reihenfolge der Auswahl wird auf den Etiketten gedruckt"
          >
            <Select
              showSearch={{ filterOption: antdTokenFilter }}
              allowClear
              mode="multiple"
              placeholder="Suche nach Analysestationen"
              options={[...analysisStations]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(it => ({
                  label: it.name,
                  value: it.id,
                  search: it.name,
                }))}
            />
          </AntForm.Item>
        </Col>
      </Row>
      <Row gutter={[16, 0]}>
        <Col span={24}>
          <AntForm.Item name="formIds" label="Formulare">
            <Select
              showSearch={{ filterOption: antdTokenFilter }}
              allowClear
              mode="multiple"
              placeholder="Suche nach Formularen"
              options={[...forms]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(it => ({
                  label: it.name,
                  value: it.id,
                  search: it.name,
                }))}
            />
          </AntForm.Item>
        </Col>
      </Row>
      <Row gutter={[16, 0]}>
        <Col span={12}>
          <Space orientation="vertical">
            <AntForm.Item name="requiresLocalizations" valuePropName="checked" noStyle>
              <Checkbox>Benötigt Lokalisationen</Checkbox>
            </AntForm.Item>
            <AntForm.Item name="localizationInputEnabled" valuePropName="checked" noStyle>
              <Checkbox>Freitext-Lokalisationen erlaubt</Checkbox>
            </AntForm.Item>
          </Space>
        </Col>
        <Col span={12}>
          <AntForm.Item
            name="predefinedLocalizations"
            label="Vorgegebene Lokalisationen"
            rules={[
              {
                type: 'array',
                min: requiresLocalizations ? (localizationInputEnabled ? 0 : 1) : 0,
                message: 'Wenn keine Freitext-Lokalisationen erlaubt sind, dann ist mind. eine Vorgabe nötig.',
              },
              // mirrors SpecimenService.validatePredefinedLocalizations - without this the admin
              // gets a raw GraphQL error on save instead of a field error while typing
              {
                validator: (_, value: string[] | undefined) => {
                  const entries = value ?? [];
                  if (entries.some(it => !it.trim())) {
                    return Promise.reject('Lokalisationen dürfen nicht leer sein.');
                  }
                  // trimmed, like SpecimenService.validatePredefinedLocalizations - otherwise
                  // "Auge" and "Auge " pass here and fail server-side with a raw GraphQL error
                  if (new Set(entries.map(it => it.trim())).size !== entries.length) {
                    return Promise.reject('Lokalisationen müssen eindeutig sein.');
                  }
                  if (entries.some(it => it.length > MAX_LOCALIZATION_LENGTH)) {
                    return Promise.reject(`Lokalisationen dürfen höchstens ${MAX_LOCALIZATION_LENGTH} Zeichen haben.`);
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <PredefinedLocalizationsEditor />
          </AntForm.Item>
        </Col>
      </Row>
      <Row gutter={[16, 0]}>
        <Col span={24}>
          <AntForm.Item
            name="localizationFields"
            label="Zusatzfelder je Lokalisation"
            rules={[
              // mirrors SpecimenService.validateLocalizationFields, including its case-insensitive
              // duplicate check - SpmLocalizationWriter matches the "Menge" label that way
              {
                validator: (_, value: { label: string }[] | undefined) => {
                  const labels = (value ?? []).map(it => it.label);
                  if (labels.some(it => !it.trim())) {
                    return Promise.reject('Zusatzfelder brauchen eine Bezeichnung.');
                  }
                  if (new Set(labels.map(it => it.trim().toLowerCase())).size !== labels.length) {
                    return Promise.reject('Bezeichnungen müssen eindeutig sein.');
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <LocalizationFieldsEditor />
          </AntForm.Item>
        </Col>
      </Row>
      {/* hack for enter button support */}
      <Button htmlType="submit" hidden />
    </AntForm>
  );
};
